import React from 'react';
import PropTypes from 'prop-types';

import { Alert } from '@openedx/paragon';
import { Report } from '@openedx/paragon/icons';
import { useSelector } from 'react-redux';

import { getAuthenticatedUser } from '@edx/frontend-platform/auth';
import { useIntl } from '@edx/frontend-platform/i18n';

import { AvatarOutlineAndLabelColors } from '../../data/constants';
import {
  selectUserHasModerationPrivileges, selectUserIsGroupTa, selectUserIsStaff,
} from '../data/selectors';
import messages from '../post-comments/messages';
import AlertBar from './AlertBar';

const AlertBanner = ({
  author,
  abuseFlagged,
  lastEdit,
  closed,
  closedBy,
  closeReason,
  editByLabel,
  closedByLabel,
  postData,
}) => {
  const intl = useIntl();
  const userHasModerationPrivileges = useSelector(selectUserHasModerationPrivileges);
  const userIsGroupTa = useSelector(selectUserIsGroupTa);
  const userIsGlobalStaff = useSelector(selectUserIsStaff);
  const userIsContentAuthor = getAuthenticatedUser().username === author;
  const canSeeReportedBanner = abuseFlagged;
  const canSeeLastEditOrClosedAlert = (userHasModerationPrivileges || userIsGroupTa
    || userIsGlobalStaff || userIsContentAuthor
  );
  // Extract first role for color lookup (handles comma-separated multi-role strings)
  const firstEditByRole = editByLabel?.split(',')[0]?.trim();
  const firstClosedByRole = closedByLabel?.split(',')[0]?.trim();
  const editByLabelColor = AvatarOutlineAndLabelColors[firstEditByRole];
  const closedByLabelColor = AvatarOutlineAndLabelColors[firstClosedByRole];

  return (
    <>
      {canSeeReportedBanner && (
        <Alert icon={Report} variant="danger" className="px-3 mb-1 py-10px shadow-none flex-fill">
          {intl.formatMessage(messages.abuseFlaggedMessage)}
        </Alert>
      )}
      { canSeeLastEditOrClosedAlert && (
        <>
          {lastEdit?.reason && (
            <AlertBar
              message={intl.formatMessage(messages.editedBy)}
              author={lastEdit.editorUsername}
              authorLabel={editByLabel}
              labelColor={editByLabelColor && `text-${editByLabelColor}`}
              reason={lastEdit.reason}
              postData={postData}
            />
          )}
          {closed && (
            <AlertBar
              message={intl.formatMessage(messages.closedBy)}
              author={closedBy}
              authorLabel={closedByLabel}
              labelColor={closedByLabelColor && `text-${closedByLabelColor}`}
              reason={closeReason}
              postData={postData}
            />
          )}
        </>
      )}
    </>
  );
};

AlertBanner.propTypes = {
  author: PropTypes.string.isRequired,
  abuseFlagged: PropTypes.bool,
  closed: PropTypes.bool,
  closedBy: PropTypes.string,
  closedByLabel: PropTypes.string,
  closeReason: PropTypes.string,
  editByLabel: PropTypes.string,
  lastEdit: PropTypes.shape({
    editorUsername: PropTypes.string,
    reason: PropTypes.string,
  }),
  postData: PropTypes.shape({}),
};

AlertBanner.defaultProps = {
  abuseFlagged: false,
  closed: undefined,
  closedBy: undefined,
  closedByLabel: undefined,
  closeReason: undefined,
  editByLabel: undefined,
  lastEdit: {},
  postData: null,
};

export default React.memo(AlertBanner);
